import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import Papa from "papaparse";
import apiManager from "../../utils/apiManager";
import fallbackManager from "../../utils/fallbackManager";

const FlightContext = createContext();

// const AIRPORT_CSV_PATH = (typeof window !== 'undefined'
//   ? new URL('/airports.csv', window.location.origin).toString()
//   : '/airports.csv');
//new CDN URL for faster loading
const AIRPORT_CSV_PATH = 'https://miczon.sgp1.cdn.digitaloceanspaces.com/LiveFlightTracker/airports/airports.csv';
const INITIAL_CHUNK_SIZE = 2000; // Larger initial chunk for faster rendering
const CHUNK_LOAD_INTERVAL = 2000; // 3 seconds between chunks
const API_KEY = 'c5d5b0-742c7e';

export const FlightProvider = ({ children }) => {
  const [flights, setFlights] = useState([]);
  const [allFlights, setAllFlights] = useState([]);
  const [airportData, setAirportData] = useState([]);
  const [airportIndex, setAirportIndex] = useState(new Map());
  const [loading, setLoading] = useState(false); // Changed to false - map shows immediately
  const [flightDetails] = useState([]);
  const [airportDataLoaded, setAirportDataLoaded] = useState(false);
  const [currentChunkIndex, setCurrentChunkIndex] = useState(0);
  const [isLoadingFlights, setIsLoadingFlights] = useState(false); // Keep for compatibility but not used
  const [showingDummyFlights, setShowingDummyFlights] = useState(true); // Track if showing dummy flights
  const [initRequested, setInitRequested] = useState(false);
  const [initStarted, setInitStarted] = useState(false);

  // Map specific states
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [totalDistance, setTotalDistance] = useState(0);
  const [coveredDistance, setCoveredDistance] = useState(0);
  const [mapCenter, setMapCenter] = useState([25.8617, 115.1954]); // Default to China center
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [viewportBounds, setViewportBounds] = useState(null);
  const [isMobile, setIsMobile] = useState(false);


  // Define getAirportInfo with O(1) lookup
  const getAirportInfo = useCallback((iataCode) => {
    if (!iataCode) return null;
    
    // Fast lookup using indexed Map
    if (airportIndex && airportIndex.has(iataCode)) {
      return airportIndex.get(iataCode);
    }

    // Fallback while data is loading
    return {
      city: iataCode,
      country: 'Unknown',
      airportName: `${iataCode} Airport`,
      latitude: 0,
      longitude: 0
    };
  }, [airportIndex]);

  // Process CSV rows (used in worker callback and fallback when worker unavailable)
  const processAirportRows = useCallback((rows) => {
    return rows
      .filter((row) => row && row.length >= 7)
      .map((row) => {
        const lat = parseFloat(row[5]);
        const lng = parseFloat(row[6]);
        if (isNaN(lat) || isNaN(lng) || !row[2] || !row[3]) return null;
        return {
          city: row[0] || 'Unknown City',
          country: row[1] || 'Unknown Country',
          depCode: String(row[2]).trim().toUpperCase(),
          arrCode: String(row[3]).trim().toUpperCase(),
          airportName: row[4] || 'Unknown Airport',
          latitude: lat,
          longitude: lng,
        };
      })
      .filter(Boolean);
  }, []);

  const loadCSVData = useCallback(() => {
    console.log('🔄 Loading airport CSV data...');
    setAirportDataLoaded(false);

    Papa.parse(AIRPORT_CSV_PATH, {
      download: true,
      header: false,
      worker: true,
      complete: (result) => {
        console.log('📡 CSV download complete, processing data...');
        if (!result || !result.data || result.data.length === 0) {
          console.error("No data found in CSV");
          setAirportDataLoaded(false);
          return;
        }

        const rows = result.data;

        const applyResult = (structuredData) => {
          if (structuredData.length === 0) {
            console.error("No valid data after processing CSV");
            setAirportDataLoaded(false);
            return;
          }
          
          // Index the data for O(1) lookup performance
          const index = new Map();
          structuredData.forEach(airport => {
            if (airport.depCode) index.set(airport.depCode, airport);
            if (airport.arrCode) index.set(airport.arrCode, airport);
          });
          
          setAirportIndex(index);
          setAirportData(structuredData);
          setAirportDataLoaded(true);
          console.log(`✅ Loaded and indexed ${structuredData.length} airports from CSV`);
        };

        try {
          if (typeof Worker !== 'undefined' && typeof import.meta !== 'undefined' && import.meta.url) {
            const worker = new Worker(new URL('../../workers/airportData.worker.js', import.meta.url));
            worker.onmessage = (e) => {
              if (e.data && e.data.type === 'done') {
                applyResult(e.data.data);
              }
              worker.terminate();
            };
            worker.onerror = () => {
              applyResult(processAirportRows(rows));
            };
            worker.postMessage({ type: 'process', rows });
          } else {
            applyResult(processAirportRows(rows));
          }
        } catch (_) {
          applyResult(processAirportRows(rows));
        }
      },
      error: (error) => {
        console.error("❌ Error loading CSV:", error);
        setAirportDataLoaded(false);
      },
    });
  }, [processAirportRows]);

  const fetchFlights = useCallback(async (bounds = null) => {
    console.log('🚀 fetchFlights called');
    
    // Start with dummy flights immediately
    const dummyFlights = fallbackManager.getInitialDummyFlights(100);
    setFlights(dummyFlights);
    setAllFlights(dummyFlights);
    setShowingDummyFlights(true);
    setIsLoadingFlights(true);
    
    // Set timeout for 4 seconds - if API takes longer, keep dummy flights
    const timeoutId = setTimeout(() => {
      if (showingDummyFlights) {
        console.log('⏰ API timeout - keeping dummy flights');
        setIsLoadingFlights(false);
      }
    }, 4000);
    
    try {
      // Reduce initial fetch to 5000 for better performance; full 10k only if needed for global view
      const maxGlobalFlights = (window.location.pathname === '/' || window.location.pathname === '/live-flights') ? 10000 : 5000;
      console.log(`🌐 Calling Aviation Edge API (Limit: ${maxGlobalFlights})...`);
      const allFlightsData = await apiManager.getAviationEdgeFlights(maxGlobalFlights);
      console.log('📡 API Response received:', allFlightsData?.length, 'flights');
      
      // Clear timeout since we got data
      clearTimeout(timeoutId);
      
      if (Array.isArray(allFlightsData) && allFlightsData.length > 0) {
        // Process flights immediately - only basic validation
        const validFlights = [];
        // Use a high-performance for loop for large arrays to avoid multiple filter/map passes
        for (let i = 0; i < allFlightsData.length; i++) {
          const f = allFlightsData[i];
          if (f && f.flight?.iataNumber && f.geography?.latitude && f.geography?.longitude) {
            validFlights.push(f);
          }
        }

        // Expert Tip: Prevent unnecessary re-renders by checking for meaningful changes
        const currentFlights = flights || [];
        // Check length and sampled positions to detect real moves without full array comparison
        const isDataEffectivelySame = 
          validFlights.length === currentFlights.length && 
          validFlights[0]?.geography?.latitude === currentFlights[0]?.geography?.latitude &&
          validFlights[Math.floor(validFlights.length / 2)]?.geography?.latitude === currentFlights[Math.floor(currentFlights.length / 2)]?.geography?.latitude &&
          validFlights[validFlights.length - 1]?.geography?.latitude === currentFlights[currentFlights.length - 1]?.geography?.latitude;

        if (!isDataEffectivelySame || showingDummyFlights) {
          setAllFlights(validFlights);
          setFlights(validFlights);
          setShowingDummyFlights(false);
          console.log(`✅ Updated flight data (${validFlights.length} flights)`);
        }
        setIsLoadingFlights(false);
      } else {
        console.warn('❌ No valid flight data received');
        setIsLoadingFlights(false);
        // Keep dummy flights
      }
    } catch (error) {
      console.error("❌ Error fetching flight data:", error);
      clearTimeout(timeoutId);
      setIsLoadingFlights(false);
      // Keep dummy flights on error
    }
  }, []);

  const fetchAirlineTimetable = useCallback(async (airlineIata, onBatch = null) => {
    try {
      // Use the new API manager for airline timetable with incremental updates
      const flightData = await apiManager.getAirlineTimetable(airlineIata, (currentFlights) => {
        // Process each batch as it arrives
        const processedBatch = currentFlights.map(flight => {
          const depAirport = getAirportInfo(flight.departure?.iataCode);
          const arrAirport = getAirportInfo(flight.arrival?.iataCode);
          
          return {
            ...flight,
            departure: {
              ...flight.departure,
              airportName: depAirport?.airportName || flight.departure?.iataCode,
              city: depAirport?.city || 'Loading...',
              country: depAirport?.country || ''
            },
            arrival: {
              ...flight.arrival,
              airportName: arrAirport?.airportName || flight.arrival?.iataCode,
              city: arrAirport?.city || 'Loading...',
              country: arrAirport?.country || ''
            }
          };
        }).sort((a, b) => {
          const numA = parseInt(a.flight?.number) || 0;
          const numB = parseInt(b.flight?.number) || 0;
          return numA - numB;
        });

        if (typeof onBatch === 'function') {
          onBatch(processedBatch);
        }
      });
      
      return flightData;
    } catch (error) {
      console.error('Error fetching airline timetable:', error);
      return [];
    }
  }, [getAirportInfo]);

  // Show dummy flights immediately when component mounts
  useEffect(() => {
    console.log('🛩️ Showing initial dummy flights immediately');
    const dummyFlights = fallbackManager.getInitialDummyFlights(50);
    setFlights(dummyFlights);
    setAllFlights(dummyFlights);
  }, []);

    // Detect mobile device with high-performance resize listener
    useEffect(() => {
      let timeoutId = null;
      const checkMobile = () => {
        const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || 
                              window.innerWidth <= 768;
        setIsMobile(isMobileDevice);
      };
      
      const debouncedCheck = () => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(checkMobile, 150);
      };
      
      checkMobile();
      window.addEventListener('resize', debouncedCheck, { passive: true });
      return () => {
        if (timeoutId) clearTimeout(timeoutId);
        window.removeEventListener('resize', debouncedCheck, { passive: true });
      };
    }, []);

  // Lazily initialize heavy CSV + flight fetch only when needed (improves mobile Web Vitals).
  // Any consumer that calls useFlightAPI() will request init automatically (see hook at bottom).
  const ensureInitialized = useCallback(() => {
    setInitRequested(true);
  }, []);

  useEffect(() => {
    if (!initRequested || initStarted) return;
    setInitStarted(true);
    try {
      loadCSVData();
    } catch (_) {}
    try {
      console.log('🔄 Fetching flights (lazy init)');
      fetchFlights();
    } catch (_) {}
  }, [initRequested, initStarted, loadCSVData, fetchFlights]);

  // No more progressive loading - flights render immediately as they're processed (like Android app)

  const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const toRad = (value) => (value * Math.PI) / 180;
    const R = 6371; // Earth's radius in kilometers

    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return parseFloat((R * c).toFixed(0)); // Distance in kilometers
  };

  const showFlightPath = (flight) => {
    if (!flight || !flight.geography) return;

    const { latitude, longitude } = flight.geography;
    const departureAirport = getAirportInfo(flight.departure.iataCode);
    const arrivalAirport = getAirportInfo(flight.arrival.iataCode);

    if (departureAirport && arrivalAirport) {
        const totalDist = haversineDistance(
            departureAirport.latitude,
            departureAirport.longitude,
            arrivalAirport.latitude,
            arrivalAirport.longitude
        );

        const coveredDist = haversineDistance(
            departureAirport.latitude,
            departureAirport.longitude,
            latitude,
            longitude
        );

        setTotalDistance(totalDist);
        setCoveredDistance(coveredDist);
    }
    
    setSelectedFlight(flight);
    setMapCenter([latitude, longitude]);
  };

  const removeFlightPath = () => {
    setSelectedFlight(null);
  };

  const fetchDetailsForFlight = useCallback(async (flightNumber) => {
    if (!flightNumber) return;
    
    setDetailsLoading(true);
    try {
      const details = flightDetails.find(
        (detail) => detail.flight?.iataNumber === flightNumber
      );
      
      if (details) {
        setDetailsLoading(false);
        return;
      }
      
      setDetailsLoading(false);
    } catch (error) {
      console.error("Error fetching flight details:", error);
      setDetailsLoading(false);
    }
  }, [flightDetails]);

  // New function to fetch flight details on-demand (like Android app)
  const fetchFlightDetailsOnDemand = useCallback(async (flight) => {
    if (!flight || !flight.flight?.iataNumber) return null;
    
    setDetailsLoading(true);
    try {
      const iataNumber = flight.flight.iataNumber;
      const flightNumber = flight.flight.number;
      
      // Determine which airport to use for the API call
      let iataCode = flight.arrival?.iataCode;
      if (!iataCode) {
        iataCode = flight.departure?.iataCode;
      }
      
      if (!iataCode || !flightNumber) {
        console.warn('Missing airport code or flight number for', iataNumber);
        return null;
      }
      
      console.log(`🔍 Fetching details for ${iataNumber} (${iataCode}, ${flightNumber})`);
      
      // Use Aviation Edge API to get detailed flight information
      const detailsData = await apiManager.getFlightDetailsFromAviationEdge(
        iataNumber, 
        iataCode, 
        flightNumber
      );
      
      if (detailsData) {
        // Enhance the flight data with detailed information
        const enhancedFlight = {
          ...flight,
          // Preserve original geography data (critical for map positioning)
          geography: flight.geography,
          // Add detailed departure information
          departure: {
            ...flight.departure,
            ...detailsData.departure,
            airportName: getAirportInfo(flight.departure?.iataCode)?.airportName || flight.departure?.airportName,
            city: getAirportInfo(flight.departure?.iataCode)?.city || flight.departure?.city,
            country: getAirportInfo(flight.departure?.iataCode)?.country || flight.departure?.country
          },
          // Add detailed arrival information
          arrival: {
            ...flight.arrival,
            ...detailsData.arrival,
            airportName: getAirportInfo(flight.arrival?.iataCode)?.airportName || flight.arrival?.airportName,
            city: getAirportInfo(flight.arrival?.iataCode)?.city || flight.arrival?.city,
            country: getAirportInfo(flight.arrival?.iataCode)?.country || flight.arrival?.country
          },
          // Add airline information
          airline: {
            ...flight.airline,
            ...detailsData.airline
          },
          // Add additional details
          codeshared: detailsData.codeshared,
          status: detailsData.status || flight.status,
          type: detailsData.type
        };
        
        console.log(`✅ Enhanced flight data for ${iataNumber}`);
        return enhancedFlight;
      }
      
      return flight; // Return original flight if no details found
    } catch (error) {
      console.error("Error fetching flight details on demand:", error);
      return flight; // Return original flight on error
    } finally {
      setDetailsLoading(false);
    }
  }, [getAirportInfo]);

  const value = React.useMemo(() => ({
    flights,
    allFlights,
    flightDetails,
    loading,
    isLoadingFlights,
    airportData,
    airportDataLoaded,
    getAirportInfo,
    showFlightPath,
    removeFlightPath,
    selectedFlight,
    totalDistance,
    coveredDistance,
    mapCenter,
    setSelectedFlight,
    detailsLoading,
    fetchDetailsForFlight,
    fetchFlightDetailsOnDemand,
    fetchAirlineTimetable,
    fetchFlights,
    ensureInitialized,
    setViewportBounds,
    isMobile,
    showingDummyFlights,
  }), [
    flights,
    allFlights,
    flightDetails,
    loading,
    isLoadingFlights,
    airportData,
    airportDataLoaded,
    getAirportInfo,
    showFlightPath,
    removeFlightPath,
    selectedFlight,
    totalDistance,
    coveredDistance,
    mapCenter,
    setSelectedFlight,
    detailsLoading,
    fetchDetailsForFlight,
    fetchFlightDetailsOnDemand,
    fetchAirlineTimetable,
    fetchFlights,
    ensureInitialized,
    setViewportBounds,
    isMobile,
    showingDummyFlights,
  ]);

  return (
    <FlightContext.Provider value={value}>
      {children}
    </FlightContext.Provider>
  );
};

export const useFlightAPI = () => {
  const ctx = useContext(FlightContext);
  const ensure = ctx?.ensureInitialized;
  useEffect(() => {
    if (typeof ensure === 'function') ensure();
  }, [ensure]);
  return ctx;
};
